export const RUNTIME_RECOVERY_EVENT = 'zwn-runtime-recovery';
const RECOVERY_FLAG = 'runtime_recovery_attempted_at';
const RECOVERY_COUNT_FLAG = 'runtime_recovery_attempts';
/*
 * Short enough to ride out a deploy, long enough that a reload cannot spin.
 *
 * This was two minutes and one attempt, ever. A deploy can outlast a single
 * recovery: the reload lands while the edge is still propagating, the second
 * failure is refused by the cooldown, and the tab sits on the error screen
 * even after the chunks come back 200. Counting attempts instead of trusting
 * one gives a stale shell three chances over a minute; a genuine code bug
 * still stops after three rather than looping forever.
 */
const RECOVERY_COOLDOWN_MS = 20 * 1000;
const RECOVERY_MAX_ATTEMPTS = 3;
// A healthy, long-lived tab must be able to recover from a later deployment.
// Keep the loop limit for one failure episode, not for the lifetime of the tab.
const RECOVERY_EPISODE_MS = 10 * 60 * 1000;
const RECOVERY_QUERY_PARAMS = ['_recover', '_trigger', '_asset'] as const;
let runtimeRecoveryListenersInstalled = false;

// Only true chunk/module load failures — NOT React DOM removeChild mismatches.
// removeChild errors are common with Maps/MUI portals during normal use and
// used to force full-page reloads while the user was just navigating the map.
// Include retryImport's own throw messages — after a Pages deploy the edge can
// hand back null/empty modules for WasteManagementDialog (and other lazy
// routes). Those used to surface as the full-app "Something went wrong" screen
// because they were not classified as recoverable.
const RUNTIME_FAILURE_RE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk \d+ failed|Importing a module script failed|Unable to preload CSS for|not a valid JavaScript MIME type|Expected a JavaScript module script|disallowed MIME type|Unexpected token '<'.*not valid JSON|Received a promise that resolves to: undefined|Lazy element type must resolve to a class or function|undefined is not an object \(evaluating '[^']*_result\.default'\)|Cannot read properties of undefined \(reading 'default'\)|Dynamic import resolved to null\/undefined module|Dynamic import resolved to empty module|Lazy module missing export/i;
const ASSET_CONTEXT_RE = /\/assets\/|Google3DGlobeView|main-[A-Za-z0-9_-]+\.js|vendor-[A-Za-z0-9_-]+\.js/i;
const ASSET_URL_RE = /\/assets\/[^?#]+\.(?:js|mjs|css)(?:[?#]|$)/i;

export function isRecoverableRuntimeFailure(message?: string, stack?: string): boolean {
  const msg = message || '';
  const trace = stack || '';
  if (!RUNTIME_FAILURE_RE.test(msg)) return false;
  if (/Unexpected token '<'/i.test(msg)) {
    return ASSET_CONTEXT_RE.test(msg) || ASSET_CONTEXT_RE.test(trace);
  }
  return true;
}

// A single transient chunk miss must NOT reload the page. `retryImport` retries the
// import 6× with backoff and normally succeeds on the next try, so tapping a FAB
// whose chunk is briefly slow/cold should show a spinner, not reload. Only escalate
// to a full reload when the SAME failures keep coming inside a short window (a
// genuinely stale/dead shell — e.g. a chunk that stays 404 through every retry).
const FAILURE_WINDOW_MS = 20 * 1000;
const FAILURE_ESCALATE_THRESHOLD = 3;
let recentFailureTimes: number[] = [];

// A single lazy import may fail several JS/CSS dependencies at once. Browser
// events for those files are not separate failed launches. Its retry owns recovery
// until it settles; only the final rejected render reaches ErrorBoundary.
let managedImports = 0;
export function beginManagedImport(): () => void {
  managedImports += 1;
  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    managedImports -= 1;
    if (!managedImports) resetRuntimeFailureWindow();
  };
}

/** Reset the in-window failure counter (used by tests and after an escalation). */
export function resetRuntimeFailureWindow(): void {
  recentFailureTimes = [];
}

/**
 * Record a chunk/asset load failure. Logs a self-confirming breadcrumb and only
 * triggers the reload-recovery once repeated failures cross the threshold — so the
 * first-tap "reload once, open on second tap" behavior is gone (retryImport retries
 * in place), while a genuinely broken shell still recovers.
 */
export function noteRuntimeFailure(trigger: string, detail = ''): void {
  if (typeof window === 'undefined') return;
  if (managedImports > 0) return; // retryImport still owns this launch.
  const now = Date.now();
  recentFailureTimes = recentFailureTimes.filter((t) => now - t < FAILURE_WINDOW_MS);
  recentFailureTimes.push(now);
  const count = recentFailureTimes.length;
  const escalate = count >= FAILURE_ESCALATE_THRESHOLD;
  try {
    console.warn(
      `[runtime-recovery] chunk-load failure #${count} (${trigger}) — ${escalate ? 'checking reload recovery' : 'retrying in place'}`,
      detail || undefined,
    );
  } catch { /* noop */ }
  if (escalate) {
    recentFailureTimes = [];
    void attemptRuntimeRecovery(trigger);
  }
}

export async function attemptRuntimeRecovery(trigger: string, beforeReload?: () => void): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  // Optional prefetches can emit several asset errors even while navigation is
  // healthy. Only a final render failure (or explicit Reload) may replace it.
  if (trigger !== 'manual-reload' && trigger !== 'error-boundary'
    && document.querySelector('[data-testid="voice-navigation-page"]')) return false;

  // The landing's globe is optional. Asset outages must not repeatedly navigate
  // away from its working scroll container or interrupt a form the visitor opened.
  // Its local boundary handles failures; an explicit reload remains available.
  if (trigger !== 'manual-reload' && document.querySelector('[data-testid="landing-scroll-root"]')) {
    return false;
  }

  try {
    const now = Date.now();
    const lastAttempt = Number(sessionStorage.getItem(RECOVERY_FLAG) || '0');
    if (lastAttempt && now - lastAttempt < RECOVERY_COOLDOWN_MS) {
      return false;
    }

    // The real loop guard. Time alone cannot tell "deploy still landing" from
    // "this build is broken"; a count can, and three is enough for the first
    // and few enough to stop the second.
    const attempts = lastAttempt && now - lastAttempt < RECOVERY_EPISODE_MS
      ? Number(sessionStorage.getItem(RECOVERY_COUNT_FLAG) || '0')
      : 0;
    if (attempts >= RECOVERY_MAX_ATTEMPTS) return false;

    sessionStorage.setItem(RECOVERY_FLAG, String(now));
    sessionStorage.setItem(RECOVERY_COUNT_FLAG, String(attempts + 1));

    // Preserve only the surface being opened; no form submissions or actions replay.
    if (trigger !== 'manual-reload') window.dispatchEvent(new Event(RUNTIME_RECOVERY_EVENT));
    // A local boundary knows the failing desk even before the parent's effects
    // commit. Record it after the generic event so an older surface cannot win.
    beforeReload?.();

    // Best-effort cleanup of stale caches / registrations before reload.
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
    } catch { /* noop */ }

    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    } catch { /* noop */ }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('_recover', String(now));
    nextUrl.searchParams.set('_trigger', trigger.slice(0, 24));
    window.location.replace(nextUrl.toString());
    return true;
  } catch {
    return false;
  }
}

export function clearRuntimeRecoveryNavigationState(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const nextUrl = new URL(window.location.href);
    let changed = false;
    for (const param of RECOVERY_QUERY_PARAMS) {
      if (nextUrl.searchParams.has(param)) {
        nextUrl.searchParams.delete(param);
        changed = true;
      }
    }
    if (!changed) return false;
    window.history.replaceState(window.history.state, document.title, nextUrl.toString());
    return true;
  } catch {
    return false;
  }
}

function getEventMessage(event: Event): string {
  const errorEvent = event as ErrorEvent;
  if (errorEvent.message) return errorEvent.message;
  const target = event.target as { src?: string; href?: string } | null;
  return target?.src || target?.href || '';
}

function getRejectionMessage(reason: unknown): { message: string; stack?: string } {
  if (typeof reason === 'string') return { message: reason };
  if (reason && typeof reason === 'object') {
    const candidate = reason as { message?: unknown; stack?: unknown };
    return {
      message: typeof candidate.message === 'string' ? candidate.message : String(reason),
      stack: typeof candidate.stack === 'string' ? candidate.stack : undefined,
    };
  }
  return { message: String(reason || '') };
}

function isAssetLoadEvent(event: Event): boolean {
  const target = event.target as { src?: string; href?: string } | null;
  const url = target?.src || target?.href || '';
  return ASSET_URL_RE.test(url);
}

export function installRuntimeRecoveryListeners(): void {
  if (runtimeRecoveryListenersInstalled || typeof window === 'undefined') return;
  runtimeRecoveryListenersInstalled = true;
  // Signal that the app's own retry-first recovery is live, so the pre-boot
  // handlers in index.html stop hard-reloading on a runtime chunk miss and defer
  // to retryImport + the debounced escalation below.
  (window as unknown as { __ZWN_BOOTED__?: boolean }).__ZWN_BOOTED__ = true;
  clearRuntimeRecoveryNavigationState();

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    const payload = (event as unknown as { payload?: { message?: string } }).payload;
    noteRuntimeFailure('vite-preload', String(payload?.message || '').slice(0, 200));
  });

  window.addEventListener('unhandledrejection', (event) => {
    const { message, stack } = getRejectionMessage(event.reason);
    if (!isRecoverableRuntimeFailure(message, stack)) return;
    event.preventDefault();
    noteRuntimeFailure('unhandled-rejection', message.slice(0, 200));
  }, true);

  window.addEventListener('error', (event) => {
    const message = getEventMessage(event);
    if (!isAssetLoadEvent(event) && !isRecoverableRuntimeFailure(message, (event as ErrorEvent).error?.stack)) {
      return;
    }
    event.preventDefault();
    noteRuntimeFailure('asset-error', message.slice(0, 200));
  }, true);
}
